import { memo, useContext, useState } from "react";
import { ObjectContext } from "@providers/ObjectProvider";
import Avatar from '@mui/joy/Avatar';
import Chip from '@mui/joy/Chip';
import Box from '@mui/joy/Box';
import Button from '@mui/joy/Button';
import ButtonGroup from '@mui/joy/ButtonGroup';
import Card from '@mui/joy/Card';
import CardContent from '@mui/joy/CardContent';
import CardOverflow from '@mui/joy/CardOverflow';
import CardActions from '@mui/joy/CardActions';
import IconButton from '@mui/joy/IconButton';
import Typography from '@mui/joy/Typography';
import SvgIcon from '@mui/joy/SvgIcon';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import UnfoldMoreRoundedIcon from '@mui/icons-material/UnfoldMoreRounded';
import UnfoldLessRoundedIcon from '@mui/icons-material/UnfoldLessRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import Tooltip from '@mui/joy/Tooltip';
import Table from '@mui/joy/Table';
import Stack from '@mui/joy/Stack';
import List from '@mui/joy/List';
import ListSubheader from '@mui/joy/ListSubheader';
import ListDivider from '@mui/joy/ListDivider';
import ListItem from '@mui/joy/ListItem';
import ListItemDecorator from '@mui/joy/ListItemDecorator';
import ListItemContent from '@mui/joy/ListItemContent';
import Sheet from '@mui/joy/Sheet';

import ObjectRelationshipCard from '@components/objects/ObjectRelationshipCard';
import { gradientTint } from "@utils/gradientTint";
import { sortByDate } from "@utils/sorters";
import { ensureDateObject, formatDate, formatDateForDatabase } from "@utils/dates";
import ObjectPreview from "./ObjectPreview";
import { useFormContext } from "react-hook-form";
import { AddRowButton } from "./AddRowButton";
import { DeleteRowButton } from "./DeleteRowButton";
import { Input, Textarea } from "@mui/joy";
import { FauxTable, FauxTableRow } from "@components/FauxTable";
import SearchSelect from "@components/SearchSelect";
import { useAuth } from "@providers/AuthProvider";
import { DatePicker } from "@components/forms/DatePicker";

const ObjectRelationshipEmployeesEdit = memo(function ObjectRelationshipEmployeesEdit({
  relationship = [],
  title,
  tint = null,
  //columns,
  collapsible = true,
}) {
  const { control, register, setValue, getValues, watch } = useFormContext();
  const data = watch(relationship) ?? [];

  const [isExpanded, setIsExpanded] = useState(false);

  const perPage = 5;

  const sortedRelationship = data;//data.sort((a, b) => sortByDate(a, b, 'created_at', 'desc'));
  const paginatedRelationship = (isExpanded || !collapsible) ? sortedRelationship : sortedRelationship.slice(0, perPage);

  const canCollapse = collapsible && sortedRelationship.length > perPage;
  const isEmpty = paginatedRelationship.length === 0;

  let paginationTitle = null;

  if(canCollapse/*collapsible && !isEmpty*/) {
    let titleText:any = <></>;//'All';
    if(data.length > perPage) {
      const ToggleIcon = isExpanded ? UnfoldMoreRoundedIcon : UnfoldLessRoundedIcon;
      titleText = <>
        {/* {`${isExpanded ? relationship.length : perPage} of ${relationship.length}`} */}
        <ToggleIcon sx={{width: '1rem', height: '1rem', mt: '-3px'}} />
      </>;
    }

    paginationTitle = (
      <Typography
        className="pagination"
        sx={theme => ({
          px: 1,
          py: 0.25,
          fontSize: 'sm',
          transition: 'var(--mm-default-transition)',
          backgroundColor: /*theme.palette.background.popup*/'#FFFFFF6B',// tint ? theme.palette[tint][200] : theme.palette.background.level2,
          borderRadius: 'sm',
        })}
      >
        {titleText}
      </Typography>
    );
  }

  const addRow = () => {
    const newRow = {
      is_unsaved: true,
      created_at: new Date(),
    };

    setValue(relationship, [...data, newRow]);
  }

  const markRowForDeletion = (rowIndex, mark = true) => {
    // let _data = data;
    // console.log(data[rowIndex]);
    data[rowIndex].to_be_deleted = mark;

    // if it's empty, just delete it
    // const { to_be_deleted, is_unsaved, ...otherProps } = data[rowIndex];
    // console.log(otherProps);
    if(data[rowIndex].is_unsaved && !data[rowIndex].note) {
      data.splice(rowIndex, 1);
    }

    setValue(relationship, data);
  }

  const setRowValue = (rowIndex, key, value) => {
    data[rowIndex][key] = value;
    setValue(relationship, data);
  };

  return (
    <ObjectRelationshipCard
      unpadded
      // empty={isEmpty}
      title={
        <CardOverflow
          sx={theme => ({
            borderTopLeftRadius: 'calc(var(--Card-childRadius) + var(--variant-borderWidth))',
            borderTopRightRadius: 'calc(var(--Card-childRadius) + var(--variant-borderWidth))',
            marginBlockStart: 'var(--CardOverflow-offset)',
            ...gradientTint(theme, tint),
          })}
        >
          <CardActions
            sx={theme => ({
              py: 0,
              // pointerEvents: canCollapse ? 'all' : 'none',
              // cursor: 'pointer',
              userSelect: 'none',
              ':hover .pagination': {
                backgroundColor: '#FFFFFFB8',
              },
            })}
          >
            <Stack
              direction="row"
              justifyContent="flex-start"
              alignItems="center"
              spacing={3}
              width={'100%'}
            >
              <Stack
                direction="row"
                justifyContent="flex-start"
                alignItems="center"
                spacing={0.5}
                width={'100%'}
                sx={{
                  pointerEvents: canCollapse ? 'all' : 'none',
                  py: 1.5,
                  cursor: 'pointer',//canCollapse ? 'pointer' : 'text',
                }}
                onClick={() => setIsExpanded(!isExpanded)}
              >
                <Typography sx={theme => ({flex: 1, fontWeight: '600', color: 'white' })}>{title}</Typography>
                {paginationTitle}
              </Stack>

              <AddRowButton onClick={addRow}/>
            </Stack>
          </CardActions>
        </CardOverflow>
      }
      sx={{
        '--mm-card-height': isExpanded ? 'auto' : '250px',
        // ...(isExpanded ? {'--mm-card-height': 'auto'} : {}),
        // ...sx
      }}
    >
      <Table
        stickyHeader
        stripe="odd"
        // hoverRow
        sx={{
          margin: 0,
          padding: 0,
          tableLayout: 'auto',
        }}
      >
        <thead>
          <tr>
            <th>Full Time</th>
            <th>Part Time</th>
            <th>Seasonal</th>
            <th>Date</th>
            <th className="min"></th>
          </tr>
        </thead>
        <tbody>
          {paginatedRelationship.length === 0 && (
            <tr>
              <td colSpan={5}>
                <Button
                startDecorator={<AddRoundedIcon/>}
                size="sm"
                variant="soft"
                color="neutral"
                onClick={addRow}
                >
                  Add Row
                </Button>
              </td>
            </tr>
          )}
        {paginatedRelationship.map((row, rowIndex:string) => {
          const isNew = !!row.is_unsaved;
          const formData = watch(data[rowIndex]) ?? null;
          const isDeleted = data[rowIndex]?.to_be_deleted ?? false;
          const isEditable = isNew;// || cell.editable === true;
          return (
            <tr key={rowIndex} className={`${isDeleted && 'to-delete'}`}>
              <td>
                <Input
                  type="number"
                  variant="outlined"
                  key={`${rowIndex}-full_time`}
                  placeholder="e.g. 5"
                  disabled={!!isDeleted}
                  value={row.full_time}
                  onChange={(e) => setRowValue(rowIndex, 'full_time', e.target.value)}
                />
              </td>
              <td>
                <Input
                  type="number"
                  variant="outlined"
                  key={`${rowIndex}-part_time`}
                  placeholder="e.g. 2"
                  disabled={!!isDeleted}
                  value={row.part_time}
                  onChange={(e) => setRowValue(rowIndex, 'part_time', e.target.value)}
                />
              </td>
              <td>
                <Input
                  type="number"
                  variant="outlined"
                  key={`${rowIndex}-seasonal`}
                  placeholder="e.g. 1"
                  disabled={!!isDeleted}
                  value={row.seasonal}
                  onChange={(e) => setRowValue(rowIndex, 'seasonal', e.target.value)}
                />
              </td>


              <td>
                {isEditable === true && (<Stack>
                  <DatePicker
                    required
                    formatDensity="spacious"
                    value={row?.date ? ensureDateObject(row?.date) : null}
                    onChange={(value) => {
                      setRowValue(rowIndex, 'date', value.target.value /*formatDateForDatabase(value)*/)
                    }}
                    // {...fieldProps}
                  />
                </Stack>)}
              </td>
              <td>
                <DeleteRowButton data={data} index={rowIndex} toggle={markRowForDeletion}/>
              </td>
            </tr>
          )
        })}
        </tbody>
      </Table>
    </ObjectRelationshipCard>
  );
});

export default ObjectRelationshipEmployeesEdit;
